import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Workflow, WorkflowExecution, ExecutionStatus } from '../types/workflow';

interface WorkflowState {
  // Current workflow selection
  selectedWorkflow: Workflow | null;
  setSelectedWorkflow: (workflow: Workflow | null) => void;

  // Current execution state
  currentExecution: WorkflowExecution | null;
  setCurrentExecution: (execution: WorkflowExecution | null) => void;
  
  // Execution progress
  executionProgress: number;
  setExecutionProgress: (progress: number) => void;
  
  currentStep: string;
  setCurrentStep: (step: string) => void;
  
  // UI state
  isExecuting: boolean;
  setIsExecuting: (executing: boolean) => void;
  
  // Form data for workflow inputs
  formData: Record<string, any>;
  setFormData: (data: Record<string, any>) => void;
  updateFormField: (field: string, value: any) => void;
  
  // Actions
  startExecution: (workflowId: string, inputs?: Record<string, any>) => void;
  stopExecution: () => void;
  resetState: () => void;
}

export const useWorkflowStore = create<WorkflowState>()(
  devtools(
    (set, get) => ({
      // Initial state
      selectedWorkflow: null,
      currentExecution: null,
      executionProgress: 0,
      currentStep: '',
      isExecuting: false,
      formData: {},

      // Setters
      setSelectedWorkflow: (workflow) => set({ selectedWorkflow: workflow }),
      setCurrentExecution: (execution) => set({ currentExecution: execution }),
      setExecutionProgress: (progress) => set({ executionProgress: progress }),
      setCurrentStep: (step) => set({ currentStep: step }),
      setIsExecuting: (executing) => set({ isExecuting: executing }),
      setFormData: (data) => set({ formData: data }),
      
      updateFormField: (field, value) => set((state) => ({
        formData: { ...state.formData, [field]: value }
      })),

      // Actions
      startExecution: (workflowId, inputs = {}) => {
        const state = get();
        set({
          isExecuting: true,
          executionProgress: 0,
          currentStep: 'Initializing...',
          formData: { ...state.formData, ...inputs }
        });
        
        // Create mock execution
        const mockExecution: WorkflowExecution = {
          execution_id: `exec-${Date.now()}`,
          workflow_id: workflowId,
          status: ExecutionStatus.RUNNING,
          current_step: 0,
          step_results: {},
          started_at: new Date().toISOString(),
          error_message: '',
        };
        
        set({ currentExecution: mockExecution });
      },

      stopExecution: () => {
        set({
          isExecuting: false,
          executionProgress: 0,
          currentStep: '',
        });
      },

      resetState: () => {
        set({
          selectedWorkflow: null,
          currentExecution: null,
          executionProgress: 0,
          currentStep: '',
          isExecuting: false,
          formData: {},
        });
      },
    }),
    {
      name: 'workflow-store',
    }
  )
);